import _ from 'lodash'
import { Observable, of, zip } from 'rxjs'
import { concatMap, map, switchMap } from 'rxjs/operators'

import { SearchResponse, ThesaurusTerm, ThesaurusTermTreeNode, DataStoreOptions } from '@skinsoft/types'
import { genericTerm, isAlternativeTerm, thesaurusRef } from '@skinsoft/selectors/thesaurus-term-selectors'
import { EntityDataStore } from '@skinsoft/entity-data-store'

declare const THESAURUS_TERMS_TO_CACHE: string[]

export class ThesaurusTermDataStore extends EntityDataStore<ThesaurusTerm> {
    public static getInstance() {
        if (!ThesaurusTermDataStore.instance) {
            ThesaurusTermDataStore.instance = new ThesaurusTermDataStore();
        }

        return ThesaurusTermDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$thesaurusTermDataStore = ThesaurusTermDataStore.getInstance();
    }

    private static instance: ThesaurusTermDataStore = null as any;

    constructor() {
        super('thesaurusTerm', ['ThesaurusTerm']);
    }

    /**
     * Search ThesaurusTerms containing a given text among children (and grandchildren) belonging to a given thesaurus
     */
    public searchOnRoot(thesaurusId: string, locale: string, searchText: string, includeChildren: boolean, page: number = 0, size: number = 100, excludedIds?: string[]): Observable<any> {
        let leftCriteria: any = {
            entityField: "59cb956f326d470001a93b3c",
            operator: "equals",
            type: "criteria",
            value: {
                refId: thesaurusId,
                type: "ref"
            }
        }

        if(!includeChildren) {
            leftCriteria = {
                leftCriteria: leftCriteria,
                linkOperator: "AND",
                rightCriteria: {
                    entityField: "59cb956f326d470001a93b3b",
                    operator: "is-missing",
                    type: "criteria",
                    value: {
                        completeSearch: false,
                        refId: null,
                        type: "ref",
                    }
                },
                type: "requestSearch",
            }
        }
        
        const body = {
            advancedSearches: [
                {
                    leftCriteria: leftCriteria,
                    linkOperator: "AND",
                    rightCriteria: {
                        leftCriteria: {
                            entityField: "599176638b87bc0001a34133", // tesaurusTermCS.preferredTerm
                            operator: "contains",
                            type: "criteria",
                            value: {
                                text: searchText,
                                type: "text"
                            }
                        },
                        linkOperator: "OR",
                        rightCriteria: {
                            entityField: "599176638b87bc0001a34131", // tesaurusTermCS.synonyms
                            operator: "contains",
                            type: "criteria",
                            value: {
                                text: searchText,
                                type: "text"
                            }
                        },
                        type: "requestSearch",
                    },
                    type: "requestSearch"
                }
            ],
            criteriaTypes: [
                "ThesaurusTerm"
            ],
            excludedIds: excludedIds,
            includeDefaultSorts: false,
            language: locale,
            linkedEntitiesField: [],
            page: page,
            pageSize: size,
            searchOnArchived: false,
            searchOnVersioned: false,
            types: [
                "ThesaurusTerm"
            ]
        }

        if(window.navigator.onLine) {
            const endpoint = '/indexSearch/paginated-search'
            return this.proxy.fetch(endpoint, undefined, 'POST', body)
        } else {
            return this.searchAllOffline(null, null, size, undefined, page, body.advancedSearches, undefined, undefined, undefined, undefined, undefined, undefined, body.language)
        }
    }

    public searchChildren(id: string, pathIds: string, locale: string, searchText: string, includeGrandChildren: boolean = true, page: number = 0, size: number = 100, excludedIds?: string[]) {
        const body = {
            advancedSearches: [
                {
                    leftCriteria: {
                        entityField: "59cb956f326d470001a93b3b",
                        operator: "equals",
                        type: "criteria",
                        value: {
                            path: pathIds,
                            refId: id,
                            searchAlternative: false,
                            searchOnChild: includeGrandChildren,
                            type: "path"
                        }
                    },
                    linkOperator: "AND",
                    rightCriteria: {
                        entityField: "599176638b87bc0001a34133",
                        operator: "contains",
                        type: "criteria",
                        value: {
                            text: searchText,
                            type: "text"
                        }
                    },
                    type: "requestSearch"
                }
            ],
            criteriaTypes: [
                "thesaurusTerm"
            ],
            excludedIds: excludedIds ? excludedIds : null,
            includeDefaultSorts: false,
            isFederateSearch: false,
            isPendingMedia: false,
            language: locale,
            page: page,
            pageSize: size,
            searchNotOnlySubject: false,
            searchOnArchived: false,
            searchOnVersioned: false,
            sortWithId: false,
            types: [
                "thesaurusTerm"
            ],
            useOCR: false
        }
        
        if(window.navigator.onLine) {
            const endpoint = '/indexSearch/paginated-search'
            return this.proxy.fetch(endpoint, undefined, 'POST', body)
        } else {
            return this.searchAllOffline(null, null, size, undefined, page, body.advancedSearches)
        }
    }

    public fetchChildren(id: string, locale: string, page: number = 0, size: number = 100) {
        if(window.navigator.onLine) {
            return this.fetchAll({
                endpoint: `/thesaurusTerm/${id}/children?page=${page}&size=${size}&lang=${locale}`,
            });
        }
        else {
            return this.fetchAllFromPredicate(thesaurusTerm => {
                return genericTerm(thesaurusTerm) == id &&
                    isAlternativeTerm(thesaurusTerm) !== true
            }).pipe(
                switchMap(children => {
                    const startPagination = page * size
                    return of(children.slice(startPagination, startPagination + size))
                })
            )
        }
    }

    public fetchParents(id: string): Observable<ThesaurusTermTreeNode> {
        return this.fetchOne(id).pipe(
            // @ts-ignore
            switchMap(thesaurusTerm => {
                const thesaurusTreeNode: ThesaurusTermTreeNode = {
                    childrenTerms: [],
                    term: thesaurusTerm
                }

                // @ts-ignore
                const genericTermId = thesaurusTerm.thesaurusTermSkin?.genericTerm
                if(genericTermId) {
                    return zip(of(thesaurusTreeNode), this.fetchParents(genericTermId))
                }

                return of([thesaurusTreeNode, null])
            }),
            switchMap(([child, parent]) => {
                if(parent) {
                    parent.childrenTerms.push(child as ThesaurusTermTreeNode)
                    return of(parent)
                }

                return of(child as ThesaurusTermTreeNode)
            })
        )
    }

    public fetchRoot(id: string) {
        if(window.navigator.onLine) {
            return this.fetchAll({
                endpoint: '/thesaurusTerm/root',
                query: {
                    entity: id,
                    institution: localStorage.getItem('institution')
                },
            })
        }
        else {
            return this.fetchAllFromPredicate(thesaurusTerm => {
                return thesaurusRef(thesaurusTerm) === id &&
                    isAlternativeTerm(thesaurusTerm) !== true &&
                    genericTerm(thesaurusTerm) == null
            })
        }
    }

    public fetchMany2(ids: string[], locale: string, page: number = 0, size: number = 100) {
        if(window.navigator.onLine) {
            const endpoint = '/indexSearch/paginated-search'
            const body = {
                advancedSearches: null,
                criteriaTypes: [
                    "ThesaurusTerm"
                ],
                filteredIds: ids,
                excludedIds: null,
                includeDefaultSorts: false,
                language: locale,
                linkedEntitiesField: [],
                page: page,
                pageSize: size,
                searchOnArchived: false,
                searchOnVersioned: false,
                types: [
                    "ThesaurusTerm"
                ]
            }
    
            return this.proxy.fetch(endpoint, undefined, 'POST', body)

        } else {
            return this.fetchAllFromPredicate(thesaurusTerm => ids.includes(thesaurusTerm.id)).pipe(
                map(results => {
                    return {
                        data: results,
                        totalSize: results.length,
                    }
                })
            )
        }
    }

    public synchronizeAll(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, options: DataStoreOptions = {}, thesaurusToIncludeIds?: string[]): Observable<SearchResponse<ThesaurusTerm>> {
        
        if(!thesaurusToIncludeIds) {
            thesaurusToIncludeIds = THESAURUS_TERMS_TO_CACHE ?? []
        }

        if(!advancedSearches) {
            advancedSearches = this.buildThesauriContentRetrievalCacheRequest(thesaurusToIncludeIds)
        }

        return super.synchronizeAll(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, options)
    }

    public create(createBody?: Partial<ThesaurusTerm>, body?: Partial<ThesaurusTerm>, locale?: string): Observable<ThesaurusTerm> {
        return super.create().pipe(
            concatMap(thesaurusTerm => {
                if (!body) {
                    return of(thesaurusTerm);
                }
                return this.update(thesaurusTerm.id, _.merge(thesaurusTerm, body), `${this.endpoint}/sharing`, undefined, locale)
            }),
            switchMap(({ id }) => this.fetchOne(id)),
        )
    }

    public update(id: string, body: Partial<ThesaurusTerm>, endpointOverride?: string, ignoreCachedStatus: boolean = false, locale?: string): Observable<ThesaurusTerm> {
        const endpoint = endpointOverride || this.endpoint;
        const uri = `${endpoint}/${id}`
        const queryParams: Record<string, any> = {
            ['language']: locale
        }
        
        return this.proxy.fetch(uri, queryParams, 'PUT', body, true).pipe(
            switchMap(response => this.fetchOne(id)),
            switchMap(async thesaurusTerm => {
                const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(id)
                if (cachedUpdatedValue) {
                    this.offlineDataUpdateCacher.removeCachedValue(id)
                }
                this.offlineDataFetcher.updateOne(id, thesaurusTerm as ThesaurusTerm);
                return thesaurusTerm
            })
        )
    }

    public setSpecificTerms(id: string, specificTerms: string[]) {
        return this.proxy.fetch(`${this.endpoint}/specific/${id}`, undefined, 'PUT', specificTerms)
    }

    private buildThesauriContentRetrievalCacheRequest(thesaurusIds: string[]) {
        let advancedSearch: any[] = []

        if(thesaurusIds && thesaurusIds.length) {
            const firstThesaurusId = thesaurusIds.shift()

            let genericTermRes: any = {
                entityField: '59cb956f326d470001a93b3b',
                operator: 'equals',
                type: 'criteria',
                value: {
                    completeSearch: false,
                    refId: firstThesaurusId,
                    type: 'ref'
                }
            }

            let thesaurusFieldRes: any = {
                entityField: '59cb956f326d470001a93b3c',
                operator: 'equals',
                type: 'criteria',
                value: {
                    completeSearch: false,
                    refId: firstThesaurusId,
                    type: 'ref'
                }
            }

            for(let thesaurusId of thesaurusIds) {
                genericTermRes = this.buildThesaurusTermAdvancedSearchCriteria('59cb956f326d470001a93b3b', genericTermRes, thesaurusId)
                thesaurusFieldRes = this.buildThesaurusTermAdvancedSearchCriteria('59cb956f326d470001a93b3c', thesaurusFieldRes, thesaurusId)
            }

            const finalResult = {
                leftCriteria: thesaurusFieldRes,
                linkOperator: 'OR',
                rightCriteria: genericTermRes,
                type: 'requestSearch'
            }

            advancedSearch.push(finalResult)
        }

        return advancedSearch
    }

    private buildThesaurusTermAdvancedSearchCriteria(entityField: string, previous: any, refId: string) {
        return {
            leftCriteria: previous,
            linkOperator: 'OR',
            rightCriteria: {
                entityField: entityField,
                operator: 'equals',
                type: 'criteria',
                value: {
                    completeSearch: false,
                    refId: refId,
                    type: 'ref'
                }
            },
            type: 'requestSearch'
        }
    }
}
